import React from 'react'
import styled from '@emotion/styled'
import { Frequency, FREQUENCY } from '@classy/campaign-page-blocks'
import { useTranslation } from 'next-i18next'
import { AppFrequencyOption } from '../../../config/state/config-types'
import { DEFAULT_THEME_COLORS } from 'css/theme'
import RecurringUpsellTag from './RecurringUpsellTag'
import { convertRgbToRGBA } from 'utils/colors'
import shimmer from './shimmer'
import { css } from '@emotion/react'
import { getFrequencyLabel } from 'utils/functions/frequencyUtils'

interface FrequencyToggleGroupProps {
  borderRadius: string
  initialFocusRef: React.RefObject<HTMLButtonElement>
  onFrequencyChange: (frequency: Frequency) => void
  primaryColor: string
  secondaryColor: string
  selectedFrequency: Frequency
  visibleFrequencies: AppFrequencyOption[]
  showRecurringUpsellTag?: boolean
  isLoading: boolean
}

export const FrequencyButtonFieldset = styled.fieldset`
  border: none;
  margin: 0;
  padding: 0;
  display: flex;
  border-radius: 24px;
  background-color: var(--computed-color__brand-primary--tint);
  padding: 4px;
`

// Toggle Group with connected buttons
const ToggleGroupContainer = styled.div<{ borderRadius: string; secondaryColor: string }>`
  display: flex;
  justify-content: center;
  background-color: ${({ secondaryColor }) =>
    secondaryColor || 'var(--classy-custom-secondary-color, #e8ebf9)'};
  border-radius: ${({ borderRadius }) => borderRadius};
  margin-bottom: 24px;
  border: 2px solid rgba(0, 0, 0, 0);
`

const ToggleButton = styled.button<{
  active: boolean
  borderRadius: string
  primaryColor: string
  isLoading: boolean
}>`
  flex: 1;
  padding: 12px 24px;
  box-shadow: ${({ active, isLoading }) =>
    isLoading
      ? 'none'
      : active
      ? 'var(--classy-shadow__active, 0 2px 4px rgba(0, 0, 0, 0.2))'
      : 'none'};
  background-color: ${({ active, primaryColor }) =>
    active ? primaryColor || 'var(--classy-custom-primary-color, #364fc7)' : 'rgba(0,0,0,0)'};
  color: ${({ active }) =>
    active
      ? 'var(--computed-color__brand-primary--contrast, #ffffff)'
      : DEFAULT_THEME_COLORS['--theme-color--neutral-10']};
  border: none;
  border-radius: ${({ borderRadius }) => borderRadius};
  font-size: 16px;
  font-weight: bold;
  line-height: 24px;
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  ${({ isLoading, borderRadius, primaryColor }) =>
    isLoading &&
    css`
      position: relative;
      color: transparent;
      pointer-events: none;
      background-color: #dcdcdc;
      background-image: linear-gradient(
        90deg,
        ${convertRgbToRGBA(primaryColor, 0.1)} 20%,
        ${convertRgbToRGBA(primaryColor, 0.6)} 50%,
        ${convertRgbToRGBA(primaryColor, 0.1)} 80%
      );
      background-size: 400% 100%;
      animation: ${shimmer} 1.5s infinite linear;
      background-clip: padding-box;
      border-radius: ${borderRadius};
      box-sizing: border-box;
      line-height: 1;
    `}
  &:hover {
    opacity: 0.9;
  }
  text-align: center;
  text-underline-position: from-font;
  text-decoration-skip-ink: none;
`

const CheckIcon = styled.svg<{
  color: string
  width?: number
  height?: number
}>`
  fill: ${({ color }) => color || 'currentColor'};
  width: ${({ width }) => width || 12}px;
  height: ${({ height }) => height || 12}px;
  transition: all 0.2s ease-in-out;
`

const CheckIconContainer = () => (
  <CheckIcon
    color={'var(--computed-color__brand-primary--contrast, #ffffff)'}
    width={12}
    height={12}
    xmlns="http://www.w3.org/2000/svg"
    viewBox="6 7 13 10"
  >
    <path d="M10.5 16.5c-.1 0-.2-.02-.4-.07-.12-.05-.23-.12-.34-.23L6.27 12.7c-.19-.19-.28-.43-.27-.72.01-.28.1-.51.3-.7.19-.19.43-.28.72-.28s.53.09.72.28l2.83 2.83L17.35 7.3c.19-.19.42-.28.72-.28.28-.01.52.09.74.3.19.19.28.42.28.7 0 .28-.09.52-.28.71l-7.54 7.54c-.11.11-.23.18-.34.23-.13.05-.25.07-.39.07z" />
  </CheckIcon>
)

const FrequencyToggleButton: React.FC<{
  frequency: Frequency
  initialFocusRef: React.RefObject<HTMLButtonElement>
  isActive: boolean
  onClick: () => void
  primaryColor: string
  borderRadius: string
  label: string
  showRecurringUpsellTag?: boolean
  isLoading: boolean
}> = ({
  frequency,
  isActive,
  onClick,
  primaryColor,
  borderRadius,
  label,
  showRecurringUpsellTag,
  isLoading,
}) => (
  <div style={{ display: 'flex', width: '100%', position: 'relative' }}>
    {showRecurringUpsellTag && <RecurringUpsellTag primaryColor={primaryColor} />}
    <ToggleButton
      primaryColor={primaryColor}
      key={frequency}
      active={isActive}
      borderRadius={borderRadius}
      data-id={`${frequency}-frequency-button`}
      onClick={onClick}
      isLoading={isLoading}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '6px',
      }}
      aria-pressed={isActive}
    >
      {isActive && !isLoading && <CheckIconContainer />}
      {label}
    </ToggleButton>
  </div>
)

const FrequencyToggleGroup: React.FC<FrequencyToggleGroupProps> = ({
  borderRadius,
  initialFocusRef,
  onFrequencyChange,
  primaryColor,
  secondaryColor,
  selectedFrequency,
  visibleFrequencies,
  showRecurringUpsellTag,
  isLoading,
}) => {
  const { t } = useTranslation('common')

  const visibleRecurringFrequencies = visibleFrequencies.filter(
    (frequency) => frequency.name !== FREQUENCY.ONE_TIME,
  )

  if (!visibleFrequencies.find((frequency) => frequency.name === FREQUENCY.ONE_TIME)) {
    return null
  }

  return (
    <ToggleGroupContainer borderRadius={borderRadius} secondaryColor={secondaryColor}>
      {isLoading ? (
        <FrequencyToggleButton
          frequency={FREQUENCY.ONE_TIME}
          initialFocusRef={initialFocusRef}
          isActive={selectedFrequency === FREQUENCY.ONE_TIME}
          onClick={() => onFrequencyChange(FREQUENCY.ONE_TIME)}
          primaryColor={primaryColor}
          borderRadius={borderRadius}
          label={getFrequencyLabel(FREQUENCY.ONE_TIME)}
          isLoading={isLoading}
        />
      ) : (
        <>
          <FrequencyToggleButton
            frequency={FREQUENCY.ONE_TIME}
            initialFocusRef={initialFocusRef}
            isActive={selectedFrequency === FREQUENCY.ONE_TIME}
            onClick={() => onFrequencyChange(FREQUENCY.ONE_TIME)}
            primaryColor={primaryColor}
            borderRadius={borderRadius}
            label={getFrequencyLabel(FREQUENCY.ONE_TIME)}
            isLoading={isLoading}
          />

          {visibleRecurringFrequencies.length === 1 && (
            <FrequencyToggleButton
              frequency={visibleRecurringFrequencies[0].name}
              initialFocusRef={initialFocusRef}
              isActive={selectedFrequency !== FREQUENCY.ONE_TIME}
              onClick={() => onFrequencyChange(visibleRecurringFrequencies[0].name)}
              primaryColor={primaryColor}
              borderRadius={borderRadius}
              label={
                visibleRecurringFrequencies[0].name === FREQUENCY.BI_WEEKLY ||
                visibleRecurringFrequencies[0].name === FREQUENCY.SEMI_ANNUALLY
                  ? t('FrequencyToggleGroup.every.label', 'Every')
                  : getFrequencyLabel(visibleRecurringFrequencies[0].name)
              }
              showRecurringUpsellTag={showRecurringUpsellTag}
              isLoading={isLoading}
            />
          )}

          {visibleRecurringFrequencies.length > 1 && (
            <FrequencyToggleButton
              frequency={visibleRecurringFrequencies[0].name}
              initialFocusRef={initialFocusRef}
              isActive={selectedFrequency !== FREQUENCY.ONE_TIME}
              onClick={() => onFrequencyChange(visibleRecurringFrequencies[0].name)}
              primaryColor={primaryColor}
              borderRadius={borderRadius}
              label={t('FrequencyToggleGroup.recurring.label', 'Recurring')}
              showRecurringUpsellTag={showRecurringUpsellTag}
              isLoading={isLoading}
            />
          )}
        </>
      )}
    </ToggleGroupContainer>
  )
}

export default FrequencyToggleGroup
